import { Parameter } from '@arra/core';
import styled from '@emotion/styled';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MovingIcon from '@mui/icons-material/Moving';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import {
  AddressInfo,
  fetchAddressById,
  getAddressLabel,
  getDistance,
  searchAddress,
  TransportMode,
  transportOptions,
} from './distanceUtils';

interface DistanceFrom2PointsProps {
  parameter: Parameter;
  onChange: (value: string) => void;
  disabled: boolean;
}

const DistanceContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DistanceFrom2Points = ({
  parameter,
  onChange,
  disabled = false
}: DistanceFrom2PointsProps) => {
  const [state, setState] = useState({
    address1: null as AddressInfo | null,
    address2: null as AddressInfo | null,
    transport: TransportMode.Car,
    distance: null as number | null,
  });

  const [helper, setHelper] = useState('');

  const parseInitialValue = async () => {
    if (parameter?.value) {
      const parts = String(parameter.value).split(';');
      if (parts.length === 4) {
        const [distStr, id1, id2, trans] = parts;
        const [addr1, addr2] = await Promise.all([
          fetchAddressById(id1),
          fetchAddressById(id2),
        ]);
        setState({
          address1: addr1,
          address2: addr2,
          transport: Object.values(TransportMode).includes(
            trans as TransportMode,
          )
            ? (trans as TransportMode)
            : TransportMode.Car,
          distance: parseFloat(distStr),
        });
      }
    }
  };

  useEffect(() => {
    if (!state.address1) {
      parseInitialValue();
    }
  }, []);

  useEffect(() => {
    const calculateDistance = async () => {
      const { address1, address2, transport } = state;
      const dist = await getDistance(address1, address2, transport);
      if (address1 && address2) {
        if (dist !== null) {
          setState((prev) => ({ ...prev, distance: dist }));
          setHelper('');
        } else {
          setHelper('La distance n\'a pu être calculée.');
        }
      } else {
        setHelper('Renseigner les adresses de départ et d\'arrivée afin de calculer automatiquement la distance.');
      }
      onChange(
        `${dist || ''};${address1?.id || ''};${address2?.id || ''
        };${transport}`,
      );
    };
    calculateDistance();
  }, [state.address1, state.address2, state.transport]);

  return (
    <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
      <TransportToggle
        transport={state.transport}
        setTransport={(t) => setState((prev) => ({ ...prev, transport: t }))}
        disabled={disabled}
      />
      <AddressInput
        label="Adresse de départ"
        value={state.address1}
        onSelect={(val) => setState((prev) => ({ ...prev, address1: val }))}
        disabled={disabled}
      />
      <AddressInput
        label="Adresse d'arrivée"
        value={state.address2}
        onSelect={(val) => setState((prev) => ({ ...prev, address2: val }))}
        disabled={disabled}
      />

      {helper && (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {helper}
        </Typography>
      )}

      {state.distance !== null && !helper && (
        <Typography variant="body2" color="success.main">
          <DistanceContainer>
            <MovingIcon />
            Distance : {state.distance.toFixed(2)} km
          </DistanceContainer>
        </Typography>
      )}
    </Box>
  );
};

export const AddressInput = ({
  label,
  onSelect,
  value,
  disabled = false,
}: {
  label: string;
  onSelect: (val: AddressInfo | null) => void;
  value: AddressInfo | null;
  disabled?: boolean;
}) => {
  const [options, setOptions] = useState<AddressInfo[]>([]);
  const [input, setInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 600);


  useEffect(() => {
    if (value) setInput(value.label);
  }, [value]);

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length > 3) {
      searchAddress(debouncedSearchTerm).then(setOptions);
    }
  }, [debouncedSearchTerm]);

  const needsCompletion = input.length === 0 && !value;
  const needsSelection = options.length > 0 && !value;


  return (
    <Autocomplete
      options={options}
      filterOptions={(options) => options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : getAddressLabel(option)
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={value}
      inputValue={input}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          setSearchTerm(newInputValue);
        }
        setInput(newInputValue);
      }}
      onChange={(event, newValue) => {
        setOptions([]);
        if (typeof newValue === 'string') return;
        onSelect(newValue || null);
        setInput(getAddressLabel(newValue));

      }}
      onClose={() => {
        setOptions([]);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {needsSelection ? (
                  <Typography variant="caption" color="error">
                    Sélectionnez une adresse
                  </Typography>
                ) : null}
                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
          }}
          helperText={needsCompletion
            ? "Commencez à taper puis choisissez une adresse."
            : ""}
          error={needsSelection}
          disabled={disabled}
        />
      )}
    />
  );
};

const StyledToggleButton = styled(ToggleButton)`
  padding: 12px;

  @media (max-width: 1400px) {
    padding: 8px;

    svg {
      font-size: 1.2rem;
    }
  }

  @media (max-width: 1200px) {
    padding: 6px;

    svg {
      font-size: 1.1rem;
    }
  }
`;


interface TransportToggleProps {
  transport: TransportMode;
  setTransport: (transport: TransportMode) => void;
  disabled: boolean;
}

const TransportToggle = ({ transport, setTransport, disabled = false }: TransportToggleProps) => {
  return (
    <ToggleButtonGroup
      value={transport}
      exclusive
      onChange={(e, newTransport) => {
        if (newTransport !== null) {
          setTransport(newTransport as TransportMode);
        }
      }}
      disabled={disabled}
    >
      {transportOptions.map((option) => (
        <StyledToggleButton
          key={option.value}
          value={option.value}
          disabled={disabled}
        >
          <Tooltip title={option.label} arrow>
            {option.icon}
          </Tooltip>
        </StyledToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default DistanceFrom2Points;
